import React from 'react';
import { sendEvent } from '../Tracking';
import { PageBreakerModule } from './PageBreakerModule';
import { RelatedLinksModule } from './RelatedLinksModule';
import { FAQLinksModule } from './FAQLinksModule';
import { MiniCarouselNoHeroModule } from './MiniCarouselNoHeroModule';
import { HeaderModule } from './HeroHeaderModule';
import { ModuleCarousel } from './ModuleCarousel';
import { SingleTestimonialModule } from './SingleTestimonialModule';
import { TwoUpModule } from './TwoUpFeaturedContentModule';
import { ThreeUpModule } from './ThreeUpFeaturedContentModule';
import { HeroNarrativeCarouselModule } from './HeroNarrativeCarouselModule';
import { HeroBodyModule } from './HeroBodyModule';
import { HeroTestimonial } from './HeroNarrativeCarouselModule/Options/HeroTestimonial';
import { HeroDescription } from './HeroNarrativeCarouselModule/Options/HeroDescription';
import { HeroCaseStudy } from './HeroNarrativeCarouselModule/Options/HeroCaseStudy';
import { GroupTestimonialModule } from './GroupTestimonialModule';
import { EventListModule } from './EventListModule';
import { EditorialGridModule } from './EditorialGridModule';
import { VideoModule } from './VideoModule';
import { RelatedStoriesListModule } from './RelatedStoriesListModule';
import { ArticleHeaderModule } from './ArticleHeaderModule';
import { ArticleModule } from './ArticleModule';
import { FeaturedContentModule } from './FeaturedContentModule';
import { AnimationContext, useAnimations, customThreshold } from '../ui';

export type ModuleContentType =
  | 'articleModule'
  | 'eventsListModule'
  | 'faqLinksModule'
  | 'pageBreakerModule'
  | 'editorialGridModule'
  | 'singleFeatureModule'
  | 'singleTestimonial'
  | 'groupTestimonial'
  | 'featuredContent2Up'
  | 'featuredContent3up'
  | 'relatedLinksModule'
  | 'heroBodyModule'
  | 'headerModule'
  | 'heroBodyCarousel'
  | 'heroHeaderCarousel'
  | 'heroNarrativeModule'
  | 'heroNarrativeCarousel'
  | 'heroNarrativeQuoteSlide'
  | 'heroNarrativeTextSlide'
  | 'heroCaseStudyCover'
  | 'videoModule'
  | 'featuredContentModule'
  | 'articleHeaderModule';

// base fields supported by all module types
export type ModuleType = {
  contentType: ModuleContentType;
  shouldAnimate?: boolean;
  id?: string;
  title?: string;
  themeName?: string;
  backgroundColor?: string;
  textColor?: string;
  isHeader?: boolean;
  isVisible?: boolean;
  ref?: React.MutableRefObject<Element | undefined>;
  author?: string;
  releaseDate?: string;
  introduction?: string;
  article?: string;
};

const MODULE_COMPONENTS: { [key: string]: React.FC<any> } = {
  articleModule: ArticleModule,
  faqLinksModule: FAQLinksModule,
  pageBreakerModule: PageBreakerModule,
  editorialGridModule: EditorialGridModule,
  singleFeatureModule: MiniCarouselNoHeroModule,
  singleTestimonial: SingleTestimonialModule,
  groupTestimonial: GroupTestimonialModule,
  featuredContent2Up: TwoUpModule,
  featuredContent3up: ThreeUpModule,
  relatedLinksModule: RelatedLinksModule,
  headerModule: HeaderModule,
  heroBodyModule: HeroBodyModule,
  heroBodyCarousel: ModuleCarousel,
  heroHeaderCarousel: ModuleCarousel,
  heroNarrativeCarousel: HeroNarrativeCarouselModule,
  heroNarrativeQuoteSlide: HeroTestimonial,
  heroNarrativeTextSlide: HeroDescription,
  heroCaseStudyCover: HeroCaseStudy,
  eventList: EventListModule,
  relatedStoriesList: RelatedStoriesListModule,
  videoModule: VideoModule,
  featuredContentModule: FeaturedContentModule,
  articleHeaderModule: ArticleHeaderModule,
};

export const Module = React.forwardRef<
  React.MutableRefObject<ModuleType>,
  ModuleType
>((props, ref) => {
  const {
    contentType,
    shouldAnimate = true,
    isVisible = false,
    title = '',
  } = props;
  const ModuleComponent = MODULE_COMPONENTS[contentType];
  const shown = React.useRef(false);
  const { animationContext, animationRef } = useAnimations(
    contentType,
    shouldAnimate,
    customThreshold(contentType),
  );

  React.useEffect(() => {
    if (isVisible && ModuleComponent && !shown.current) {
      shown.current = true;
      sendEvent({
        eventCategory: `Frodor - module-${contentType}`,
        eventAction: 'view',
        eventLabel: title,
      });
    }
  }, [isVisible]);

  if (ModuleComponent) {
    return (
      <AnimationContext.Provider value={animationContext}>
        <ModuleComponent {...props} ref={mergeRefs(ref, animationRef)} />
      </AnimationContext.Provider>
    );
  }
  return <>Module type "{contentType}" not found</>;
});

const mergeRefs = (
  ...refs: Array<React.MutableRefObject<any> | Function | null>
) => {
  const filteredRefs: Array<
    React.MutableRefObject<any> | Function | null
  > = refs.filter(Boolean);
  if (!filteredRefs.length) return null;
  if (filteredRefs.length === 0) return filteredRefs[0];
  return (inst: any) => {
    for (const elementRef of filteredRefs) {
      if (typeof elementRef === 'function') {
        elementRef(inst);
      } else if (elementRef) {
        elementRef.current = inst;
      }
    }
  };
};

export { FAQLinksModule } from './FAQLinksModule';
export { PageBreakerModule } from './PageBreakerModule';
export { EditorialGridModule } from './EditorialGridModule';
export { MiniCarouselNoHeroModule } from './MiniCarouselNoHeroModule';
export { RelatedLinksModule } from './RelatedLinksModule';
export { HeaderModule } from './HeroHeaderModule';
export { HeroBodyModule } from './HeroBodyModule';
export { ModuleCarousel } from './ModuleCarousel/ModuleCarousel';
export { SingleTestimonialModule } from './SingleTestimonialModule';
export { GroupTestimonialModule } from './GroupTestimonialModule';
export { TwoUpModule } from './TwoUpFeaturedContentModule';
export { ThreeUpModule } from './ThreeUpFeaturedContentModule';
export { HeroNarrativeCarouselModule } from './HeroNarrativeCarouselModule';
export { HeroTestimonial } from './HeroNarrativeCarouselModule/Options/HeroTestimonial';
export { HeroDescription } from './HeroNarrativeCarouselModule/Options/HeroDescription';
export { HeroCaseStudy } from './HeroNarrativeCarouselModule/Options/HeroCaseStudy';
export { EventListModule } from './EventListModule';
export { VideoModule } from './VideoModule';
export { RelatedStoriesListModule } from './RelatedStoriesListModule';
export { ArticleModule } from './ArticleModule';
export { ArticleHeaderModule } from './ArticleHeaderModule';
export { FeaturedContentModule } from './FeaturedContentModule';
